import "./index.css";

import { HelpMessage } from "@ids/react-help-message";
import type { InputFieldProps } from "@ids/react-input-field";
import { Loader } from "@ids/react-loader";
import classNames from "classnames";
import {
  type ChangeEvent,
  type FC,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import type { InputNecessity } from "@/utils/types";
import { ErrorMessage } from "../errorMessage/ErrorMessage";
import { Popover } from "../popover/Popover";
import { KeyCodes } from "./constants";
import type { AutoCompleteSelectItem } from "./types";

export interface AutocompleteFieldProps extends InputFieldProps {
  children?: React.ReactNode;
  setQuery: (query: string) => void;
  suggestions: AutoCompleteSelectItem[];
  onSuggestionSelected: (
    suggestion: AutoCompleteSelectItem | undefined,
    reset?: boolean,
  ) => void;
  onClear?: () => void;
  necessity?: InputNecessity;
  inputValue: string;
  placeholder?: string;
  popoverHeader?: string | React.ReactNode;
  popoverMessage?: string | React.ReactNode;
  helpMessage?: string;
  errorMessage?: string | false;
  isLoading?: boolean;
  dataTestid?: string;
}

const getHighlightedText = (text: string, highlight: string) => {
  // Escape regex special characters in highlight using String.raw
  const escapeRegExp = (str: string) =>
    str.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
  const safeHighlight = escapeRegExp(highlight);
  const parts = text.split(new RegExp(`(${safeHighlight})`, "gi"));
  return (
    <>
      {parts
        .filter((m) => m !== "")
        .map((part, index) => {
          const key = `${part}-${index}`;
          return part.toLowerCase() === highlight.toLowerCase() ? (
            <strong
              key={key}
              dangerouslySetInnerHTML={{
                __html: part.replaceAll(" ", "&nbsp;"),
              }}
            ></strong>
          ) : (
            <span
              key={key}
              dangerouslySetInnerHTML={{
                __html: part.replaceAll(" ", "&nbsp;"),
              }}
            ></span>
          );
        })}
    </>
  );
};

export const ComposedAutoComplete: FC<Readonly<AutocompleteFieldProps>> = ({
  id,
  children,
  necessity,
  disabled,
  dataTestid,
  helpMessage,
  errorMessage,
  popoverHeader,
  popoverMessage,
  placeholder = "",
  name,
  setQuery,
  suggestions,
  inputValue,
  isLoading,
  onSuggestionSelected,
  onClear,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLMenuElement>(null);

  const disabledClass = disabled ? "is-disabled" : undefined;
  const errorMessageId = `${id}-error-message`;
  const helpMessageId = `${id}-help-message`;
  const labelId = `${id}-label`;
  const invalid = !!errorMessage;
  const ariaDescribedby = invalid ? errorMessageId : helpMessageId;

  const [state, setState] = useState({
    isDropdownOpen: false,
    focusedIndex: -1,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    if (value.trim() === "") {
      onClear?.();
      onSuggestionSelected(undefined, true);
    }
    setState((prevState) => ({
      ...prevState,
      isDropdownOpen: true,
    }));
  };

  const handleFocus = () => {
    setState((prevState) => ({
      ...prevState,
      isDropdownOpen: true,
    }));
  };

  const handleSuggestionClick = (suggestion: AutoCompleteSelectItem) => {
    setState((prevState) => ({
      ...prevState,
      isDropdownOpen: false,
      focusedIndex: -1,
    }));
    if (onSuggestionSelected) {
      onSuggestionSelected(suggestion);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    let newIndex = state.focusedIndex;
    switch (e.key) {
      case KeyCodes.ARROW_DOWN:
        if (!state.isDropdownOpen) {
          newIndex = 0;
          setState((prevState) => ({ ...prevState, isDropdownOpen: true }));
        } else {
          newIndex = (state.focusedIndex + 1) % suggestions.length;
        }
        break;
      case KeyCodes.ARROW_UP:
        newIndex =
          (state.focusedIndex - 1 + suggestions.length) % suggestions.length;
        break;
      case KeyCodes.ENTER:
        if (state.focusedIndex >= 0) {
          e.preventDefault();
          handleSuggestionClick(suggestions[state.focusedIndex]);
        } else {
          setState((prevState) => ({ ...prevState, isDropdownOpen: false }));
        }
        break;
      case KeyCodes.ARROW_LEFT:
      case KeyCodes.ARROW_RIGHT:
      case KeyCodes.TAB:
      case KeyCodes.ESCAPE:
        setState((prevState) => ({ ...prevState, isDropdownOpen: false }));
        break;
      case KeyCodes.PAGE_UP:
        newIndex = 0;
        break;
      case KeyCodes.PAGE_DOWN:
        newIndex = suggestions.length - 1;
        break;
      default:
        return;
    }

    setState((prevState) => ({
      ...prevState,
      focusedIndex: newIndex,
    }));
  };

  const handleBlur = () => {
    const suggestion = suggestions.find(
      (suggestion) =>
        suggestion.label.toLowerCase() === inputValue.toLowerCase(),
    );
    if (suggestion) {
      onSuggestionSelected(suggestion);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setState((prevState) => ({
          ...prevState,
          isDropdownOpen: false,
          focusedIndex: -1,
        }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // For scrolling the suggestions
  useEffect(() => {
    if (state.focusedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(
        `#option-${state.focusedIndex}`,
      ) as HTMLElement;
      if (highlightedElement) {
        const dropdown = dropdownRef.current;
        if (dropdown) {
          const { offsetTop, clientHeight } = highlightedElement;
          const { scrollTop, clientHeight: dropdownHeight } = dropdown;

          if (offsetTop < scrollTop) {
            dropdown.scrollTop = offsetTop;
          } else if (offsetTop + clientHeight > scrollTop + dropdownHeight) {
            dropdown.scrollTop = offsetTop + clientHeight - dropdownHeight;
          }
        }
      }
    }
  }, [state.focusedIndex]);

  const classes = classNames("if input-field", { "is-invalid": invalid });
  const labelClasses = classNames(
    "if label flex-row align-items-center justify-content-space-between",
    necessity,
    disabledClass,
  );

  return (
    <div className={`auto-complete-container`}>
      <label id={labelId} className={labelClasses} htmlFor={id}>
        <span>{children}</span>
        <Popover
          popoverHeader={popoverHeader}
          popoverMessage={popoverMessage}
        />
      </label>
      <div className="if position-relative">
        <input
          className={classes}
          id={id}
          name={name}
          type="text"
          autoComplete="off"
          placeholder={placeholder}
          value={inputValue}
          role="combobox"
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          aria-describedby={ariaDescribedby}
          aria-autocomplete="list"
          aria-controls={`${id}-dropdown-list`}
          aria-haspopup="listbox"
          onBlur={handleBlur}
          aria-activedescendant={
            state.focusedIndex >= 0 ? `option-${state.focusedIndex}` : ""
          }
          aria-expanded={state.isDropdownOpen}
          disabled={disabled || isLoading}
          data-testid={dataTestid}
        />
        {isLoading && (
          <span className="auto-complete-loader">
            <Loader type="dots" size="small" />
          </span>
        )}
        <menu
          id={`${id}-dropdown-list`}
          ref={dropdownRef}
          className={classNames(
            "if composed-auto-complete dropdown-list full-width",
            {
              "is-open": state.isDropdownOpen,
            },
          )}
          aria-hidden={!state.isDropdownOpen}
          aria-live="polite"
        >
          {suggestions.map((suggestion, index) => (
            <button
              type="button"
              className={`if dropdown-list-item highlighted-text`}
              key={suggestion.id}
              id={`option-${index}`}
              tabIndex={-1}
              onMouseDown={() => handleSuggestionClick(suggestion)}
            >
              {getHighlightedText(suggestion.label, inputValue)}
            </button>
          ))}
        </menu>
      </div>
      {!invalid && helpMessage && (
        <HelpMessage
          id={helpMessageId}
          data-testid={dataTestid ? `${dataTestid}-help-message` : undefined}
          disabled={disabled}
        >
          {helpMessage}
        </HelpMessage>
      )}
      {invalid && errorMessage && (
        <ErrorMessage
          id={errorMessageId}
          data-testid={dataTestid ? `${dataTestid}-error-message` : undefined}
        >
          {errorMessage}
        </ErrorMessage>
      )}
    </div>
  );
};
